import { useMemo } from "react";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";

/**
 * What the current admin is allowed to do in the changelog tab.
 *
 * Drafting and publishing are deliberately separate grants: the person who
 * rewrites a bullet is not necessarily the person allowed to put it in front of
 * customers. Each flag mirrors one API route, including the super-tenant bypass
 * that `requirePermission` applies server-side, so the UI never offers a button
 * the API would refuse — or hides one it would allow.
 */
export interface ChangelogPermissions {
  canList: boolean;
  canSync: boolean;
  canRegenerate: boolean;
  canEdit: boolean;
  canApprove: boolean;
  canRevokeApproval: boolean;
  canSend: boolean;
  canListRecipients: boolean;
  canAddRecipient: boolean;
  canUpdateRecipient: boolean;
  canRemoveRecipient: boolean;
  canViewSettings: boolean;
  canEditSettings: boolean;
  canTestSlack: boolean;
  canTestAlertsSlack: boolean;
  canListDigests: boolean;
  canCompose: boolean;
  canListCandidates: boolean;
  canEditDigest: boolean;
  canDeleteDigest: boolean;
  canRegenerateDigest: boolean;
  canApproveDigest: boolean;
  canRevokeDigestApproval: boolean;
  canSendDigest: boolean;
}

export function useChangelogPermissions(): ChangelogPermissions {
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();

  return useMemo(
    () => ({
      canList: isSuperTenant || perms.ADMIN_LIST_CHANGELOG.isOn,
      canSync: isSuperTenant || perms.ADMIN_SYNC_CHANGELOG.isOn,
      canRegenerate: isSuperTenant || perms.ADMIN_REGENERATE_CHANGELOG.isOn,
      canEdit: isSuperTenant || perms.ADMIN_EDIT_CHANGELOG.isOn,
      canApprove: isSuperTenant || perms.ADMIN_APPROVE_CHANGELOG.isOn,
      canRevokeApproval: isSuperTenant || perms.ADMIN_REVOKE_CHANGELOG_APPROVAL.isOn,
      canSend: isSuperTenant || perms.ADMIN_SEND_CHANGELOG.isOn,
      canListRecipients: isSuperTenant || perms.ADMIN_LIST_CHANGELOG_RECIPIENTS.isOn,
      canAddRecipient: isSuperTenant || perms.ADMIN_ADD_CHANGELOG_RECIPIENT.isOn,
      canUpdateRecipient: isSuperTenant || perms.ADMIN_UPDATE_CHANGELOG_RECIPIENT.isOn,
      canRemoveRecipient: isSuperTenant || perms.ADMIN_REMOVE_CHANGELOG_RECIPIENT.isOn,
      canViewSettings: isSuperTenant || perms.ADMIN_GET_CHANGELOG_SETTINGS.isOn,
      canEditSettings: isSuperTenant || perms.ADMIN_UPDATE_CHANGELOG_SETTINGS.isOn,
      canTestSlack: isSuperTenant || perms.ADMIN_TEST_CHANGELOG_SLACK.isOn,
      canTestAlertsSlack: isSuperTenant || perms.ADMIN_TEST_ALERTS_SLACK.isOn,
      canListDigests: isSuperTenant || perms.ADMIN_LIST_CHANGELOG_DIGESTS.isOn,
      canCompose: isSuperTenant || perms.ADMIN_COMPOSE_CHANGELOG_DIGEST.isOn,
      canListCandidates: isSuperTenant || perms.ADMIN_LIST_DIGEST_CANDIDATES.isOn,
      canEditDigest: isSuperTenant || perms.ADMIN_EDIT_CHANGELOG_DIGEST.isOn,
      canDeleteDigest: isSuperTenant || perms.ADMIN_DELETE_CHANGELOG_DIGEST.isOn,
      canRegenerateDigest: isSuperTenant || perms.ADMIN_REGENERATE_DIGEST.isOn,
      canApproveDigest: isSuperTenant || perms.ADMIN_APPROVE_CHANGELOG_DIGEST.isOn,
      canRevokeDigestApproval: isSuperTenant || perms.ADMIN_REVOKE_DIGEST_APPROVAL.isOn,
      canSendDigest: isSuperTenant || perms.ADMIN_SEND_CHANGELOG_DIGEST.isOn,
    }),
    [perms, isSuperTenant],
  );
}
